import React, { useState } from 'react';
import { useTranslation } from '../i18n/LanguageContext';
const RequestWordPage = ({ API, userToken, isDarkMode, showNotification, setCurrentView }) => {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    word: '',
    language: 'en',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const languages = [
    { code: 'en', name: 'English', flag: '🇬🇧' },
    { code: 'fr', name: 'French', flag: '🇫🇷' },
    { code: 'es', name: 'Spanish', flag: '🇪🇸' },
    { code: 'ar', name: 'Arabic', flag: '🇸🇦' }
  ];

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const formDataToSend = new FormData();
      formDataToSend.append('word', formData.word);
      formDataToSend.append('language', formData.language);
      formDataToSend.append('message', formData.message);
      formDataToSend.append('token', userToken);

      const response = await fetch(`${API}/suggest-word`, {
        method: 'POST',
        body: formDataToSend
      });

      if (response.ok) {
        showNotification('✓ ' + t('req_sent_ok'), 'success');
        setFormData({ word: '', language: 'en', message: '' });
      } else {
        const error = await response.json();
        throw new Error(error.detail || t('req_send_failed'));
      }
    } catch (error) {
      console.error('Error submitting suggestion:', error);
      showNotification(error.message || t('req_send_failed'), 'error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="max-w-2xl mx-auto p-4">
      <div className={`mb-6 ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
        {setCurrentView && (
          <button onClick={() => setCurrentView('translator')}
            className={`flex items-center gap-1 mb-3 px-2 py-1 rounded-lg text-sm font-medium transition-all ${isDarkMode ? 'hover:bg-gray-800 text-gray-400' : 'hover:bg-gray-100 text-gray-600'}`}>
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            {t('back')}
          </button>
        )}
        <h1 className="text-3xl font-bold bg-gradient-to-r from-green-600 to-teal-600 bg-clip-text text-transparent">
          📝 {t('req_request_new_word')}
        </h1>
        <p className={`text-sm mt-2 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
          {t('req_cant_find')}
        </p>
      </div>
      
      <div className={`rounded-2xl shadow-xl ${isDarkMode ? 'bg-gray-800' : 'bg-white'}`}>
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {/* Word Input */}
          <div>
            <label className={`block text-sm font-medium mb-1 ${
              isDarkMode ? 'text-gray-300' : 'text-gray-700'
            }`}>
              {t('req_word_looking')} *
            </label>
            <input
              type="text"
              value={formData.word}
              onChange={(e) => setFormData({...formData, word: e.target.value})}
              className={`w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : 'border-gray-300'
              }`}
              placeholder={t('req_word_placeholder')}
              required
            />
          </div>

          {/* Language Select */}
          <div>
            <label className={`block text-sm font-medium mb-1 ${
              isDarkMode ? 'text-gray-300' : 'text-gray-700'
            }`}>
              {t('req_language')} *
            </label>
            <select
              value={formData.language}
              onChange={(e) => setFormData({...formData, language: e.target.value})}
              className={`w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : 'border-gray-300'
              }`}
              required
            >
              {/* Add Darija first */}
              <option value="darija">🇲🇦 Darija (Moroccan Arabic)</option>
              
              {/* Then other languages */}
              {languages.map(lang => (
                <option key={lang.code} value={lang.code}>
                  {lang.flag} {lang.name}
                </option>
              ))}
            </select>
          </div>

          {/* Additional Message */}
          <div>
            <label className={`block text-sm font-medium mb-1 ${
              isDarkMode ? 'text-gray-300' : 'text-gray-700'
            }`}>
              {t('req_additional_msg')}
            </label>
            <textarea
              value={formData.message}
              onChange={(e) => setFormData({...formData, message: e.target.value})}
              className={`w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none ${
                isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : 'border-gray-300'
              }`}
              placeholder={t('req_msg_placeholder')}
              rows="4"
            />
          </div>

          {/* Buttons */}
          <div className="flex gap-3 pt-2">
            <button
              type="submit"
              disabled={isSubmitting}
              className="flex-1 px-6 py-3 bg-gradient-to-r from-blue-600 to-blue-700 text-white rounded-lg hover:from-blue-700 hover:to-blue-800 transition-all disabled:opacity-50 disabled:cursor-not-allowed font-semibold"
            >
              {isSubmitting ? '⏳ ' + t('req_sending') : '📤 ' + t('req_send_to_admin')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};


export default RequestWordPage;
